import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import SalarySlipSummaryCard from "./components/SalarySlipSummaryCard";
import { salarySlipService } from "../../services";
import PageSkeleton from "../../components/ui/PageSkeleton";
import Button from "../../components/ui/Button";
import {
  ArrowBack,
  Edit,
  Download,
  Delete,
  AttachMoney,
} from "@mui/icons-material";

function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [salarySlip, setSalarySlip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    // Don't make API call if id is undefined
    if (!id || id === "undefined") {
      toast.error("Invalid salary slip ID");
      navigate("/salary-slip");
      return;
    }

    const loadSalarySlip = async () => {
      setLoading(true);
      try {
        const { data } = await salarySlipService.get(id);
        setSalarySlip(data);
      } catch (err) {
        console.error("Failed to load salary slip", err);
        toast.error("Salary slip not found");
        navigate("/salary-slip");
      } finally {
        setLoading(false);
      }
    };

    loadSalarySlip();
  }, [id, navigate]);

  const handleDownloadPdf = async () => {
    if (!salarySlip || !id || id === "undefined") {
      return;
    }

    setDownloading(true);
    try {
      const response = await salarySlipService.downloadPdf(id);
      const filename = resolveFilenameFromHeaders(
        response.headers,
        buildFallbackFilename(salarySlip)
      );
      const blob = new Blob([response.data], {
        type: response.headers?.["content-type"] || "application/pdf",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Failed to download PDF", err);
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!salarySlip || !id || id === "undefined") {
      return;
    }

    const confirmation = window.confirm(
      "Delete this salary slip? This action cannot be undone."
    );
    if (!confirmation) {
      return;
    }

    setDeleting(true);
    try {
      await salarySlipService.remove(id);
      toast.success("Salary slip deleted");
      navigate("/salary-slip");
    } catch (err) {
      console.error("Failed to delete salary slip", err);
      toast.error(err?.response?.data?.error || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageSkeleton type="card" />
      </div>
    );
  }

  if (!salarySlip) {
    return null;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-2">
              Salary Slip Details
            </h1>
            <p className="text-sm sm:text-base text-gray-600 truncate">
              {salarySlip.employee_name} - {salarySlip.month || "General Template"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              variant="secondary"
              onClick={() => navigate("/salary-slip")}
              className="flex items-center justify-center gap-2 flex-1 sm:flex-initial"
            >
              <ArrowBack className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Back to List</span>
              <span className="sm:hidden">Back</span>
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate(`/salary-slip/${id}/edit`)}
              className="flex items-center justify-center gap-2 flex-1 sm:flex-initial"
            >
              <Edit className="w-4 h-4 sm:w-5 sm:h-5" />
              Edit
            </Button>
            <Button
              variant="secondary"
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="flex items-center justify-center gap-2 flex-1 sm:flex-initial"
            >
              <Download className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">{downloading ? "Downloading…" : "Download PDF"}</span>
              <span className="sm:hidden">{downloading ? "Loading…" : "PDF"}</span>
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center justify-center gap-2 flex-1 sm:flex-initial"
            >
              <Delete className="w-4 h-4 sm:w-5 sm:h-5" />
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </div>

        {/* Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <SalarySlipSummaryCard salarySlip={salarySlip} />
        </motion.div>

        {/* Salary Components */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
            Salary Components
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <InfoItem label="Basic Salary" value={salarySlip.basic_salary} />
            <InfoItem label="HRA" value={salarySlip.hra} />
            <InfoItem label="Allowances" value={salarySlip.allowances} />
            <InfoItem label="Overtime" value={salarySlip.overtime_amount} />
            <InfoItem
              label="Professional Tax"
              value={salarySlip.professional_tax}
            />
            <InfoItem
              label="PF Contribution"
              value={salarySlip.pf_contribution}
            />
            <InfoItem label="ESI" value={salarySlip.esi} />
            <InfoItem label="TDS" value={salarySlip.tds} />
            <InfoItem
              label="Gross Salary"
              value={salarySlip.gross_salary}
              highlight
            />
            <InfoItem
              label="Total Deductions"
              value={salarySlip.total_deductions}
              highlight
            />
            <InfoItem
              label="Net Salary"
              value={salarySlip.net_salary}
              highlight
            />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

const currency = (value) => {
  const num = Number(value || 0);
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const resolveFilenameFromHeaders = (headers, fallback) => {
  const disposition = headers?.["content-disposition"];
  if (!disposition) {
    return fallback;
  }

  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch && utfMatch[1]) {
    try {
      return decodeURIComponent(utfMatch[1]).trim();
    } catch (err) {
      console.warn("Failed to decode UTF-8 filename", err);
    }
  }

  const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
  if (asciiMatch && asciiMatch[1]) {
    return asciiMatch[1].trim();
  }

  return fallback;
};

const buildFallbackFilename = (slip) => {
  const name = (slip.employee_name || "Employee").replace(/\s+/g, " ").trim();
  const monthLabel = (slip.month ? dayjs(`${slip.month}-01`) : dayjs())
    .format("MMM YYYY")
    .toUpperCase();
  return `Pay Slips- ${name} ${monthLabel}.pdf`;
};

function InfoItem({ label, value, highlight = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`p-4 rounded-2xl ${
        highlight
          ? "bg-gradient-to-br from-primary/10 to-secondary/10 border-2 border-primary/20"
          : "bg-gray-50 border border-gray-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        {highlight && <AttachMoney className="w-5 h-5 text-primary" />}
        <p className="text-sm text-gray-500 font-medium">{label}</p>
      </div>
      <p
        className={`text-xl font-bold ${
          highlight ? "text-primary" : "text-gray-700"
        }`}
      >
        {currency(value)}
      </p>
    </motion.div>
  );
}

export default DetailPage;
